import React, { useEffect, useMemo, useState } from "react";
import { Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, TextField, CircularProgress, Alert, Button, Pagination, Select, MenuItem, TableSortLabel, Box, Checkbox, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { firestore, firebaseProjectId, functions } from "../services/firebase";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import BlockIcon from "@mui/icons-material/Block";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { normalizeWhitespace } from "../lib/address";
import { MobileCard } from "../components/OSMDataAdmin/styled";
import { useIsMobile } from "../hooks/useIsMobile";
import { captureError } from "../lib/errorReporting";
import { useStateRegions, useStateRegionActions } from "../actions/stateRegions";

// Default sort matches the Firestore query order preference: alphabetical by
// name. Name is unique, so it stays pinned as the final tie-break.
const DEFAULT_SORT_MODEL = [{ field: "name", direction: "asc" }];

export function NBLBMasterList() {
    const history = useHistory();
    // The 8-column table (checkbox + 6 fields + a 3-button action cell) needs a
    // wide viewport, especially alongside the 300px admin sidebar. Switch to the
    // stacked card layout below 1024px so mobile/tablet never horizontally
    // scrolls.
    const isMobile = useIsMobile(1024);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [filter, setFilter] = useState("");
    const [pruning, setPruning] = useState(false);
    const [page, setPage] = useState(1);
    const PAGE_SIZE_OPTIONS = [25, 50, 100];
    const [pageSize, setPageSize] = useState(100);
    const [sortModel, setSortModel] = useState(DEFAULT_SORT_MODEL);
    const [excludingIds, setExcludingIds] = useState(new Set());
    // Ids optimistically hidden after an Exclude click. Kept separate from
    // `rows` so a Firestore snapshot refresh can't momentarily flash the row
    // back before the delete propagates. Rolled back on failure.
    const [removedIds, setRemovedIds] = useState(new Set());
    // Row selection for bulk actions.
    const [selectedIds, setSelectedIds] = useState(new Set());
    // Post-exclude confirmation state ({ rows, blacklistIds, message }); the
    // acknowledgement modal's Undo restores these to the master list and
    // un-blacklists them.
    const [undoState, setUndoState] = useState(null);
    // Number of venues currently being excluded (drives the immediate loading
    // modal). null when no exclude is in flight.
    const [excludeProgress, setExcludeProgress] = useState(null);
    const [undoBusy, setUndoBusy] = useState(false);
    // Auto-add flow state.
    const [addConfirm, setAddConfirm] = useState(null); // { rows }
    const [addResult, setAddResult] = useState(null); // { count, name, barId }
    const [adding, setAdding] = useState(false);
    // Optional region/subregion to assign to bars being added (single or bulk).
    const [addRegionId, setAddRegionId] = useState("");
    const [addSubregionId, setAddSubregionId] = useState("");

    const stateRegions = useStateRegions();
    const { fetchStateRegions } = useStateRegionActions();
    useEffect(() => {
        fetchStateRegions();
    }, [fetchStateRegions]);

    // Distinct states across the rows queued for add. Region assignment is only
    // offered when every bar shares one state we can match to known regions —
    // so we never stamp one state's region onto bars from another.
    const addStateInfo = useMemo(() => {
        const rows = addConfirm?.rows || [];
        const norm = (value) => String(value || "").trim().toUpperCase();
        const states = new Set(
            rows.map((row) => norm(row.addressParts?.state || row.state)).filter(Boolean)
        );
        const singleState = states.size === 1 ? [...states][0] : null;
        const matchedState = singleState
            ? (stateRegions || []).find(
                  (state) => norm(state.abbr) === singleState || norm(state.name) === singleState
              ) || null
            : null;
        return { stateCount: states.size, singleState, matchedState };
    }, [addConfirm, stateRegions]);

    const multipleStates = addStateInfo.stateCount > 1;

    // Regions for the single state the batch belongs to (empty when the batch
    // spans multiple states or the state has no configured regions).
    const regionOptions = useMemo(() => {
        const state = addStateInfo.matchedState;
        if (!state) return [];
        const options = (state.regions || []).map((region) => ({
            regionId: region.id,
            regionName: region.name,
            stateAbbr: state.abbr || state.name || state.id,
            subregions: Array.isArray(region.subregions) ? region.subregions : []
        }));
        options.sort((a, b) =>
            (a.regionName || "").localeCompare(b.regionName || "", undefined, { sensitivity: "base" })
        );
        return options;
    }, [addStateInfo]);

    const selectedRegionOption = useMemo(
        () => regionOptions.find((opt) => opt.regionId === addRegionId) || null,
        [regionOptions, addRegionId]
    );

    const addToExcludedCallable = useMemo(
        () => functions.httpsCallable("addOSMBarsToBlacklist"),
        []
    );

    const removeFromExcludedCallable = useMemo(
        () => functions.httpsCallable("removeBlacklistedVenues"),
        []
    );

    const autoAddCallable = useMemo(
        () => functions.httpsCallable("autoAddBar"),
        []
    );

    const toNumber = (value) => {
        const num = Number(value);
        return Number.isFinite(num) ? num : null;
    };

    const extractCoords = (locationValue) => {
        if (!locationValue) return { lat: null, lng: null };

        if (Array.isArray(locationValue?.coordinates) && locationValue.coordinates.length >= 2) {
            return {
                lng: toNumber(locationValue.coordinates[0]),
                lat: toNumber(locationValue.coordinates[1])
            };
        }

        if (Array.isArray(locationValue) && locationValue.length >= 2) {
            return {
                lat: toNumber(locationValue[0]),
                lng: toNumber(locationValue[1])
            };
        }

        return {
            lat: toNumber(locationValue?.lat ?? locationValue?.latitude),
            lng: toNumber(locationValue?.lng ?? locationValue?.lon ?? locationValue?.longitude)
        };
    };

    // Multi-column sort, matching the Bars page behavior. New columns are
    // inserted above Name (pinned last as the unique tie-break). Clicking a
    // sorted column flips asc -> desc, then removes it.
    const handleColumnSort = (field) => {
        setSortModel((current) => {
            const model = current.map((entry) => ({ ...entry }));
            const idx = model.findIndex((entry) => entry.field === field);
            if (idx === -1) {
                const entry = { field, direction: "asc" };
                const nameIdx = field === "name" ? -1 : model.findIndex((e) => e.field === "name");
                if (nameIdx === -1) {
                    model.push(entry);
                } else {
                    model.splice(nameIdx, 0, entry);
                }
            } else if (model[idx].direction === "asc") {
                model[idx].direction = "desc";
            } else {
                model.splice(idx, 1);
            }
            return model.length ? model : DEFAULT_SORT_MODEL;
        });
        setPage(1);
    };

    const handleCancelSort = () => {
        setSortModel(DEFAULT_SORT_MODEL);
        setPage(1);
    };

    const hasCustomSort =
        sortModel.length !== 1 ||
        !(sortModel[0].field === "name" && sortModel[0].direction === "asc");

    const renderSortLabel = (label, field) => {
        const idx = sortModel.findIndex((entry) => entry.field === field);
        const entry = idx >= 0 ? sortModel[idx] : null;
        const showOrder = Boolean(entry) && sortModel.length > 1;
        return (
            <TableSortLabel
                active={Boolean(entry)}
                direction={entry?.direction || "asc"}
                onClick={() => handleColumnSort(field)}
                hideSortIcon={false}
                title="Click to sort · click another column to add · click again to flip or remove"
                IconComponent={(props) => (
                    <ExpandMoreRoundedIcon
                        {...props}
                        fontSize="small"
                        sx={{
                            ml: 0.25,
                            transition: "transform 120ms ease",
                            transform: entry?.direction === "desc" ? "rotate(0deg)" : "rotate(180deg)"
                        }}
                    />
                )}
            >
                {label}
                {showOrder ? (
                    <Box component="span" sx={{ ml: 0.5, fontSize: "0.7rem", fontWeight: 700, opacity: 0.7 }}>
                        {idx + 1}
                    </Box>
                ) : null}
            </TableSortLabel>
        );
    };

    useEffect(() => {
        setLoading(true);
        const unsubscribe = firestore
            .collection("nblb_master")
            .orderBy("created_at", "desc")
            .limit(500)
            .onSnapshot(
                (snapshot) => {
                    const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
                    setRows(data);
                    setLoading(false);
                },
                (err) => {
                    setError(err?.message || "Failed to load master list");
                    setLoading(false);
                }
            );
        return () => unsubscribe();
    }, []);

    const runPrune = async (silent = false) => {
        if (!silent) {
            setSuccess("");
            setError("");
        }
        try {
            setPruning(true);
            // Route through the local Functions emulator in dev (keeps dev
            // isolated from the deployed prod function), otherwise the deployed
            // HTTP endpoint. Function renamed to camelCase so the emulator can
            // resolve it (hyphenated names are treated as nested groups).
            const useEmulator = process.env.REACT_APP_USE_EMULATOR === "true";
            const endpoint = useEmulator
                ? `http://localhost:5001/${firebaseProjectId}/us-central1/pruneNblbMaster`
                : `https://us-central1-${firebaseProjectId}.cloudfunctions.net/pruneNblbMaster`;
            const res = await fetch(endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                }
            });
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }
            const data = await res.json();
            const { removed, checked } = data || {};
            setSuccess(`Refreshed. Checked ${checked || 0}, removed ${removed || 0} that now exist in Supabase.`);
        } catch (err) {
            captureError(err, { view: "NBLBMasterList", action: "refreshMasterList" });
            setError(err?.message || "Failed to refresh master list");
        } finally {
            setPruning(false);
        }
    };

    useEffect(() => {
        runPrune(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formatCreatedAt = (value) => {
        if (!value) return "";
        const millis = value?.toMillis ? value.toMillis() : new Date(value).getTime();
        if (!millis || Number.isNaN(millis)) return "";
        return new Date(millis).toLocaleDateString();
    };

    const openGoogleSearch = (row) => {
        const query = [
            row.bar_name,
            row.addressParts?.street,
            row.addressParts?.city,
            row.addressParts?.state,
            row.addressParts?.postalCode
        ]
            .map((part) => (part || "").trim())
            .filter(Boolean)
            .join(" ");
        if (!query) return;
        const url = `https://www.google.com/search?q=${encodeURIComponent(query)}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const rowsWithParts = useMemo(() => {
        return rows.map((row) => {
            const hasStructured = row.address_1 || row.city || row.state || row.zip;
            if (hasStructured) {
                return {
                    ...row,
                    addressParts: {
                        street: normalizeWhitespace(row.address_1 || ""),
                        city: normalizeWhitespace(row.city || ""),
                        state: normalizeWhitespace(row.state || ""),
                        postalCode: normalizeWhitespace(row.zip || "")
                    }
                };
            }
            // Fallback: split formatted_address for legacy records
            const parts = (row.formatted_address || "").split(",").map((s) => s.trim());
            const street = parts[0] || "";
            const city = parts[1] || "";
            const stateZip = (parts[2] || "").trim().split(/\s+/);
            const state = stateZip[0] || "";
            const postalCode = stateZip[1] || "";
            return {
                ...row,
                addressParts: {
                    street: normalizeWhitespace(street),
                    city: normalizeWhitespace(city),
                    state: normalizeWhitespace(state),
                    postalCode: normalizeWhitespace(postalCode)
                }
            };
        });
    }, [rows]);

    const sortedRows = useMemo(() => {
        const getValue = (row, field) => {
            switch (field) {
                case "city": return row.addressParts?.city || "";
                case "state": return row.addressParts?.state || "";
                case "zip": return row.addressParts?.postalCode || "";
                case "street": return row.addressParts?.street || "";
                case "name": return row.bar_name || "";
                default: return "";
            }
        };
        const toMillis = (row) =>
            row?.created_at?.toMillis
                ? row.created_at.toMillis()
                : row?.created_at
                    ? new Date(row.created_at).getTime()
                    : 0;
        const activeSort = sortModel.length ? sortModel : DEFAULT_SORT_MODEL;
        const copy = [...rowsWithParts];
        copy.sort((a, b) => {
            for (const { field, direction } of activeSort) {
                let cmp;
                if (field === "created") {
                    cmp = toMillis(a) - toMillis(b);
                } else if (field === "zip") {
                    cmp = getValue(a, "zip").localeCompare(getValue(b, "zip"));
                } else {
                    cmp = getValue(a, field).localeCompare(getValue(b, field), undefined, { sensitivity: "base" });
                }
                if (cmp !== 0) return direction === "asc" ? cmp : -cmp;
            }
            // Stable tie-break by name (unique).
            return (a.bar_name || "").localeCompare(b.bar_name || "", undefined, { sensitivity: "base" });
        });
        return copy;
    }, [rowsWithParts, sortModel]);

    const filtered = useMemo(() => {
        const base = sortedRows.filter((row) => !removedIds.has(row.id));
        const f = filter.trim().toLowerCase();
        if (!f) return base;
        return base.filter((row) => {
            const name = (row.bar_name || "").toLowerCase();
            const addr = (row.formatted_address || "").toLowerCase();
            const city = (row.addressParts?.city || "").toLowerCase();
            const zip = (row.addressParts?.postalCode || "").toLowerCase();
            return name.includes(f) || addr.includes(f) || city.includes(f) || zip.includes(f);
        });
    }, [filter, sortedRows, removedIds]);

    const pageCount = useMemo(() => Math.max(1, Math.ceil(filtered.length / pageSize)), [filtered.length, pageSize]);

    useEffect(() => {
        if (page > pageCount) {
            setPage(pageCount);
        }
    }, [page, pageCount]);

    const handlePageSizeChange = (event) => {
        const next = Number(event.target.value) || PAGE_SIZE_OPTIONS[0];
        setPageSize(next);
        setPage(1);
    };

    // Exclude one or more rows: write them to the Supabase blacklist, delete
    // their nblb_master docs, and surface an Undo. Works for both the per-row
    // Exclude button and the bulk header action.
    const excludeRows = async (targetRows) => {
        const validRows = (targetRows || []).filter((row) => row?.id);
        if (!validRows.length) return;

        setError("");
        setSuccess("");
        setUndoState(null);
        // Surface the loading modal immediately so the click feels responsive.
        setExcludeProgress(validRows.length);

        const ids = validRows.map((row) => row.id);
        // Snapshot the raw doc data (minus client-only fields) so Undo can
        // recreate the master-list documents exactly as they were.
        const undoRows = validRows.map((row) => {
            const rest = { ...row };
            delete rest.addressParts;
            return rest;
        });

        // Optimistically hide immediately, preserving sort/filter/pagination.
        setRemovedIds((prev) => {
            const next = new Set(prev);
            ids.forEach((id) => next.add(id));
            return next;
        });
        setExcludingIds((prev) => {
            const next = new Set(prev);
            ids.forEach((id) => next.add(id));
            return next;
        });
        setSelectedIds((prev) => {
            const next = new Set(prev);
            ids.forEach((id) => next.delete(id));
            return next;
        });

        try {
            const response = await addToExcludedCallable({
                bars: validRows.map((row) => ({
                    bar_name: row.bar_name,
                    bar_address: row.formatted_address
                }))
            });
            const blacklistIds = response?.data?.insertedIds || [];

            await Promise.all(
                ids.map((id) => firestore.collection("nblb_master").doc(id).delete())
            );
            setRows((prev) => prev.filter((item) => !ids.includes(item.id)));

            setUndoState({
                rows: undoRows,
                blacklistIds,
                message:
                    validRows.length === 1
                        ? `Excluded "${validRows[0].bar_name || "venue"}".`
                        : `Excluded ${validRows.length} venues.`
            });
        } catch (err) {
            captureError(err, { view: "NBLBMasterList", action: "excludeVenue" });
            setError(err?.message || "Failed to exclude venue");
            // Roll back the optimistic hide since the exclude didn't complete.
            setRemovedIds((prev) => {
                const next = new Set(prev);
                ids.forEach((id) => next.delete(id));
                return next;
            });
        } finally {
            setExcludeProgress(null);
            setExcludingIds((prev) => {
                const next = new Set(prev);
                ids.forEach((id) => next.delete(id));
                return next;
            });
        }
    };

    const handleExclude = (row) => excludeRows([row]);

    // Restore the excluded rows: re-create their master-list docs and remove
    // the entries we added to the blacklist.
    const handleUndo = async () => {
        if (!undoState) return;
        setUndoBusy(true);
        setError("");
        setSuccess("");
        const { rows: undoRows, blacklistIds } = undoState;
        try {
            await Promise.all(
                undoRows.map((row) => {
                    const { id, ...data } = row;
                    return firestore.collection("nblb_master").doc(id).set(data);
                })
            );
            if (blacklistIds.length) {
                await removeFromExcludedCallable({ ids: blacklistIds });
            }
            // Un-hide the restored rows.
            setRemovedIds((prev) => {
                const next = new Set(prev);
                undoRows.forEach((row) => next.delete(row.id));
                return next;
            });
            setUndoState(null);
            setSuccess("Restored to the master list.");
        } catch (err) {
            captureError(err, { view: "NBLBMasterList", action: "undoExclude" });
            setError(err?.message || "Failed to undo exclude");
        } finally {
            setUndoBusy(false);
        }
    };

    const pagedRows = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filtered.slice(start, start + pageSize);
    }, [filtered, page, pageSize]);

    // Selection is scoped to the current filtered result set (across pages) so
    // "select all" bulk-acts on everything the user is currently looking at.
    const selectableIds = useMemo(() => filtered.map((row) => row.id), [filtered]);

    const selectedCount = useMemo(
        () => selectableIds.filter((id) => selectedIds.has(id)).length,
        [selectableIds, selectedIds]
    );

    const allSelected = selectableIds.length > 0 && selectedCount === selectableIds.length;
    const someSelected = selectedCount > 0 && !allSelected;

    const selectedRows = useMemo(
        () => filtered.filter((row) => selectedIds.has(row.id)),
        [filtered, selectedIds]
    );

    const toggleSelectAll = () => {
        setSelectedIds((prev) => {
            if (selectableIds.every((id) => prev.has(id)) && selectableIds.length > 0) {
                const next = new Set(prev);
                selectableIds.forEach((id) => next.delete(id));
                return next;
            }
            const next = new Set(prev);
            selectableIds.forEach((id) => next.add(id));
            return next;
        });
    };

    const toggleRow = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleBulkExclude = () => {
        if (!selectedRows.length) return;
        excludeRows(selectedRows);
    };

    // Open the confirmation modal before auto-adding one or more bars.
    const openAddConfirm = (targetRows) => {
        const validRows = (targetRows || []).filter((row) => row?.id);
        if (!validRows.length) return;
        setAddRegionId("");
        setAddSubregionId("");
        setAddConfirm({ rows: validRows });
    };

    const handleBulkAdd = () => openAddConfirm(selectedRows);

    // Auto-add the confirmed bars straight into Supabase, remove them from the
    // master list, and surface a success modal (with an Edit shortcut for a
    // single add).
    const confirmAdd = async () => {
        const targetRows = addConfirm?.rows || [];
        if (!targetRows.length) return;
        setAdding(true);
        setError("");
        setSuccess("");
        try {
            const regionAssignment =
                !multipleStates && addRegionId && selectedRegionOption
                    ? {
                          state: selectedRegionOption.stateAbbr,
                          regionId: addRegionId,
                          subregionId: addSubregionId || null
                      }
                    : null;
            const response = await autoAddCallable({
                bars: targetRows.map((row) => {
                    const { lat, lng } = extractCoords(row.location);
                    return {
                        masterId: row.id,
                        bar_name: row.bar_name,
                        bar_address: row.formatted_address,
                        address_1: row.addressParts?.street || row.address_1 || "",
                        city: row.addressParts?.city || row.city || "",
                        state: row.addressParts?.state || row.state || "",
                        zip: row.addressParts?.postalCode || row.zip || "",
                        latitude: lat,
                        longitude: lng
                    };
                }),
                regionAssignment
            });
            const results = response?.data?.results || [];

            // Map each input row to its result (added vs already-existing duplicate).
            const addedResults = [];
            const duplicateResults = [];
            targetRows.forEach((row, i) => {
                const result = results[i];
                if (result?.status === "duplicate") {
                    duplicateResults.push({ row, result });
                } else {
                    addedResults.push({ row, result });
                }
            });

            // Both added and duplicate rows leave the master list (added because
            // they're now live; duplicates because they already exist). The
            // server already deletes the docs; mirror it here for immediacy.
            const ids = targetRows.map((row) => row.id);
            setRemovedIds((prev) => {
                const next = new Set(prev);
                ids.forEach((id) => next.add(id));
                return next;
            });
            setRows((prev) => prev.filter((item) => !ids.includes(item.id)));
            setSelectedIds((prev) => {
                const next = new Set(prev);
                ids.forEach((id) => next.delete(id));
                return next;
            });

            const singleAdded = addedResults.length === 1 ? addedResults[0] : null;
            setAddConfirm(null);
            setAddResult({
                addedCount: addedResults.length,
                duplicateCount: duplicateResults.length,
                name: singleAdded ? singleAdded.row.bar_name || "Bar" : "",
                barId: singleAdded ? singleAdded.result?.barId || null : null
            });
        } catch (err) {
            captureError(err, { view: "NBLBMasterList", action: "autoAddBar" });
            setError(err?.message || "Failed to add bar");
            setAddConfirm(null);
        } finally {
            setAdding(false);
        }
    };

    const handleEditNewFromResult = () => {
        const barId = addResult?.barId;
        setAddResult(null);
        if (barId) history.push(`/bar/${barId}`);
    };

    const handleAddBar = (row) => {
        const name = row.bar_name || "";
        const address = row.formatted_address || "";
        const { lat, lng } = extractCoords(row.location);
        const params = new URLSearchParams();
        if (name) params.set("name", name);
        if (address) params.set("address", address);
        if (lat !== null && lng !== null) {
            params.set("lat", String(lat));
            params.set("lng", String(lng));
        }
        const search = params.toString();
        history.push({
            pathname: "/bar/new",
            search: search ? `?${search}` : "",
            state: {
                prefillBarName: name,
                prefillBarAddress: address,
                prefillLatitude: lat,
                prefillLongitude: lng,
                prefillDataSource: "nblb"
            }
        });
    };

    const renderMobileCards = () => (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {filtered.length > 0 ? (
                <Box display="flex" alignItems="center" gap={1} px={0.5}>
                    <Checkbox
                        color="primary"
                        size="small"
                        sx={{ p: 0 }}
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={toggleSelectAll}
                        inputProps={{ "aria-label": "Select all venues" }}
                    />
                    <Typography variant="body2" color="textSecondary">
                        Select all ({filtered.length})
                    </Typography>
                </Box>
            ) : null}
            {pagedRows.map((row) => (
                <MobileCard key={row.id}>
                    <div className="card-row">
                        <span className="card-label">Select</span>
                        <Checkbox
                            color="primary"
                            size="small"
                            sx={{ p: 0 }}
                            checked={selectedIds.has(row.id)}
                            onChange={() => toggleRow(row.id)}
                            inputProps={{ "aria-label": `Select ${row.bar_name || "venue"}` }}
                        />
                    </div>
                    <div className="card-row">
                        <span className="card-label">Name</span>
                        <span className="card-value" style={{ fontWeight: 600 }}>{row.bar_name}</span>
                    </div>
                    <div className="card-row">
                        <span className="card-label">Street</span>
                        <span className="card-value">{row.addressParts?.street || ""}</span>
                    </div>
                    <div className="card-row">
                        <span className="card-label">City</span>
                        <span className="card-value">{row.addressParts?.city || ""}</span>
                    </div>
                    <div className="card-row">
                        <span className="card-label">State</span>
                        <span className="card-value">{row.addressParts?.state || ""}</span>
                    </div>
                    <div className="card-row">
                        <span className="card-label">Zip</span>
                        <span className="card-value">{row.addressParts?.postalCode || ""}</span>
                    </div>
                    <div className="card-row">
                        <span className="card-label">Created</span>
                        <span className="card-value">{formatCreatedAt(row.created_at)}</span>
                    </div>
                    <Box display="flex" gap={1} mt={1} alignItems="center">
                        <Tooltip title="Look up on Google">
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={() => openGoogleSearch(row)}
                                aria-label={`Look up ${row.bar_name || "venue"} on Google`}
                            >
                                <OpenInNewRoundedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit details before adding">
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleAddBar(row)}
                                aria-label="Edit details before adding"
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Button
                            size="small"
                            variant="contained"
                            fullWidth
                            onClick={() => openAddConfirm([row])}
                            disabled={adding}
                        >
                            Add
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            fullWidth
                            startIcon={<BlockIcon />}
                            onClick={() => handleExclude(row)}
                            disabled={excludingIds.has(row.id)}
                        >
                            Exclude
                        </Button>
                    </Box>
                </MobileCard>
            ))}
            {filtered.length === 0 ? (
                <Typography variant="body2" color="textSecondary">No results</Typography>
            ) : null}
        </div>
    );

    const renderDesktopTable = () => (
        <TableContainer sx={{ maxHeight: "calc(25 * 41px + 57px)", overflow: "auto" }}>
        <Table size="small" stickyHeader>
            <TableHead>
                <TableRow>
                    <TableCell padding="checkbox">
                        <Checkbox
                            color="primary"
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={toggleSelectAll}
                            disabled={selectableIds.length === 0}
                            inputProps={{ "aria-label": "Select all venues" }}
                        />
                    </TableCell>
                    <TableCell>{renderSortLabel("Name", "name")}</TableCell>
                    <TableCell>{renderSortLabel("Street", "street")}</TableCell>
                    <TableCell>{renderSortLabel("City", "city")}</TableCell>
                    <TableCell>{renderSortLabel("State", "state")}</TableCell>
                    <TableCell>{renderSortLabel("Zip", "zip")}</TableCell>
                    <TableCell align="left" sx={{ whiteSpace: "nowrap" }}>
                        {renderSortLabel("Created At", "created")}
                    </TableCell>
                    <TableCell align="right">
                        {hasCustomSort ? (
                            <Tooltip title="Cancel sorting">
                                <IconButton
                                    size="small"
                                    onClick={handleCancelSort}
                                    aria-label="Cancel sorting"
                                >
                                    <CloseRoundedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        ) : null}
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {pagedRows.map((row) => (
                    <TableRow key={row.id} selected={selectedIds.has(row.id)} hover>
                        <TableCell padding="checkbox">
                            <Checkbox
                                color="primary"
                                checked={selectedIds.has(row.id)}
                                onChange={() => toggleRow(row.id)}
                                inputProps={{ "aria-label": `Select ${row.bar_name || "venue"}` }}
                            />
                        </TableCell>
                        <TableCell>{row.bar_name}</TableCell>
                        <TableCell>{row.addressParts?.street || ""}</TableCell>
                        <TableCell>{row.addressParts?.city || ""}</TableCell>
                        <TableCell>{row.addressParts?.state || ""}</TableCell>
                        <TableCell>{row.addressParts?.postalCode || ""}</TableCell>
                        <TableCell>{formatCreatedAt(row.created_at)}</TableCell>
                        <TableCell align="right">
                            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                                <Tooltip title="Look up on Google">
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => openGoogleSearch(row)}
                                        aria-label={`Look up ${row.bar_name || "venue"} on Google`}
                                    >
                                        <OpenInNewRoundedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit details before adding">
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handleAddBar(row)}
                                        aria-label="Edit details before adding"
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => openAddConfirm([row])}
                                    disabled={adding}
                                >
                                    Add
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<BlockIcon />}
                                    onClick={() => handleExclude(row)}
                                    disabled={excludingIds.has(row.id)}
                                >
                                    Exclude
                                </Button>
                            </Box>
                        </TableCell>
                    </TableRow>
                ))}
                {filtered.length === 0 ? (
                    <TableRow>
                        <TableCell colSpan={8}>
                            <Typography variant="body2" color="textSecondary">No results</Typography>
                        </TableCell>
                    </TableRow>
                ) : null}
            </TableBody>
        </Table>
        </TableContainer>
    );

    return (
        <PageContainer>
            <HeaderRow>
                <Button size="small" variant="outlined" onClick={() => runPrune(false)} disabled={pruning}>
                    {pruning ? "Refreshing..." : "Refresh"}
                </Button>
                {selectedCount > 0 ? (
                    <BulkActions>
                        <Typography variant="body2" color="textSecondary" className="count">
                            {selectedCount} selected
                        </Typography>
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<AddBusinessIcon />}
                            onClick={handleBulkAdd}
                            disabled={adding}
                        >
                            Add
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={<BlockIcon />}
                            onClick={handleBulkExclude}
                        >
                            Exclude
                        </Button>
                        <Button size="small" onClick={() => setSelectedIds(new Set())}>
                            Clear
                        </Button>
                    </BulkActions>
                ) : null}
            </HeaderRow>

            {error ? <Alert severity="error">{error}</Alert> : null}
            {success ? <Alert severity="success">{success}</Alert> : null}

            <Filters>
                <TextField
                    label="Filter by name or address"
                    variant="outlined"
                    size="small"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    fullWidth
                />
                <Typography variant="body2" color="textSecondary">
                    Showing {filtered.length} of {rows.length}
                </Typography>
            </Filters>

            <Paper sx={{ padding: isMobile ? 1 : 2 }}>
                {loading ? (
                    <LoadingWrap>
                        <CircularProgress size={28} />
                    </LoadingWrap>
                ) : isMobile ? renderMobileCards() : renderDesktopTable()}
            </Paper>

            <PaginationRow>
                <div className="rows-per-page">
                    <span className="label">Rows per page</span>
                    <Select size="small" value={pageSize} onChange={handlePageSizeChange}>
                        {PAGE_SIZE_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </Select>
                </div>
                <div className="page-controls">
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        className="page-summary"
                    >
                        {filtered.length === 0
                            ? "No results"
                            : `Showing ${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, filtered.length)} of ${filtered.length}`}
                    </Typography>
                    <Pagination
                        count={pageCount}
                        page={page}
                        onChange={(_, val) => setPage(val)}
                        color="primary"
                        size="small"
                        shape="rounded"
                        showFirstButton
                        showLastButton
                        siblingCount={1}
                        boundaryCount={1}
                    />
                </div>
            </PaginationRow>

            {/* Exclude: shows a loading spinner immediately, then the
                acknowledge/undo content once the exclude completes. */}
            <Dialog
                open={excludeProgress !== null || Boolean(undoState)}
                disableEscapeKeyDown
                onClose={(_, reason) => {
                    // Force an explicit choice; ignore backdrop/escape dismissals.
                    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
                }}
            >
                {excludeProgress !== null ? (
                    <>
                        <DialogTitle>
                            Excluding {excludeProgress} venue{excludeProgress === 1 ? "" : "s"}
                        </DialogTitle>
                        <DialogContent>
                            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                <CircularProgress />
                            </Box>
                        </DialogContent>
                    </>
                ) : (
                    <>
                        <DialogTitle>{undoState?.message}</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                Added to Excluded Venues. Undo to restore it to the master list.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button color="secondary" onClick={handleUndo} disabled={undoBusy}>
                                {undoBusy ? "Undoing..." : "Undo"}
                            </Button>
                            <Button variant="contained" onClick={() => setUndoState(null)} disabled={undoBusy}>
                                OK
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Add confirmation — appears before auto-adding. */}
            <Dialog open={Boolean(addConfirm)} onClose={() => !adding && setAddConfirm(null)}>
                <DialogTitle>
                    {addConfirm?.rows?.length === 1
                        ? `Add "${addConfirm.rows[0].bar_name || "this bar"}"?`
                        : `Add ${addConfirm?.rows?.length || 0} bars?`}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {addConfirm?.rows?.length === 1
                            ? "This bar will be added to the app immediately. You can edit its details afterward."
                            : "These bars will be added to the app immediately. You can edit their details afterward."}
                    </DialogContentText>
                    <Box mt={2} display="flex" flexDirection="column" gap={2}>
                        {multipleStates ? (
                            <Alert severity="info">
                                Region assignment is only available when all selected bars are in the
                                same state. These bars span multiple states, so they will be added
                                without a region.
                            </Alert>
                        ) : (
                            <>
                                <Box display="flex" flexDirection="column" gap={0.5}>
                                    <Typography variant="caption" color="textSecondary">
                                        Assign Region (optional)
                                    </Typography>
                                    <Select
                                        size="small"
                                        displayEmpty
                                        value={addRegionId}
                                        onChange={(e) => {
                                            setAddRegionId(e.target.value);
                                            setAddSubregionId("");
                                        }}
                                        disabled={adding || regionOptions.length === 0}
                                    >
                                        <MenuItem value=""><em>No region</em></MenuItem>
                                        {regionOptions.map((opt) => (
                                            <MenuItem key={opt.regionId} value={opt.regionId}>
                                                {opt.regionName} — {opt.stateAbbr}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {regionOptions.length === 0 ? (
                                        <Typography variant="caption" color="textSecondary">
                                            {addStateInfo.singleState
                                                ? `No regions are configured for ${addStateInfo.singleState}.`
                                                : "No state detected for these bars."}
                                        </Typography>
                                    ) : null}
                                </Box>
                                {addRegionId && selectedRegionOption?.subregions?.length ? (
                                    <Box display="flex" flexDirection="column" gap={0.5}>
                                        <Typography variant="caption" color="textSecondary">
                                            Assign Subregion (optional)
                                        </Typography>
                                        <Select
                                            size="small"
                                            displayEmpty
                                            value={addSubregionId}
                                            onChange={(e) => setAddSubregionId(e.target.value)}
                                            disabled={adding}
                                        >
                                            <MenuItem value=""><em>No subregion</em></MenuItem>
                                            {selectedRegionOption.subregions.map((sub) => (
                                                <MenuItem key={sub.id} value={sub.id}>{sub.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </Box>
                                ) : null}
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddConfirm(null)} disabled={adding}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={confirmAdd} disabled={adding}>
                        {adding ? "Adding..." : "Add"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add success — must be dismissed. */}
            <Dialog
                open={Boolean(addResult)}
                disableEscapeKeyDown
                onClose={(_, reason) => {
                    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
                }}
            >
                <DialogTitle>
                    {addResult?.addedCount > 0
                        ? addResult?.addedCount === 1
                            ? "Bar added"
                            : `${addResult?.addedCount} bars added`
                        : "Already in the app"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {addResult?.addedCount === 1
                            ? `"${addResult?.name}" was added to the app.`
                            : addResult?.addedCount > 1
                                ? `${addResult?.addedCount} bars were added to the app.`
                                : ""}
                        {addResult?.duplicateCount > 0
                            ? `${addResult?.addedCount > 0 ? " " : ""}${addResult?.duplicateCount} ${
                                  addResult?.duplicateCount === 1 ? "bar was" : "bars were"
                              } already in the app and ${
                                  addResult?.duplicateCount === 1 ? "was" : "were"
                              } removed from the list.`
                            : ""}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    {addResult?.barId ? (
                        <Button color="primary" startIcon={<EditIcon />} onClick={handleEditNewFromResult}>
                            Edit bar
                        </Button>
                    ) : null}
                    <Button variant="contained" onClick={() => setAddResult(null)}>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
}

const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
`;

const Filters = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
    }
`;

const LoadingWrap = styled.div`
    display: flex;
    justify-content: center;
    padding: 16px 0;
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

const BulkActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;

    .count {
        font-weight: 600;
    }
`;

const PaginationRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    padding: 12px 0;
    border-top: 1px solid #e0e0e0;

    .rows-per-page {
        display: flex;
        align-items: center;
        gap: 8px;
        font-weight: 600;
    }

    .page-controls {
        display: flex;
        align-items: center;
        gap: 12px;
        justify-content: flex-end;
        flex: 1;
        min-width: 260px;
        flex-wrap: wrap;
    }

    .page-summary {
        white-space: nowrap;
    }

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;

        .rows-per-page {
            justify-content: center;
        }

        .page-controls {
            justify-content: center;
            min-width: unset;
            flex-direction: column;
            align-items: center;
        }
    }
`;
